import { useEffect, useRef, useState } from 'react';

import { ConfirmDialog } from '../components/ui/ConfirmDialog';

export interface ConfirmOptions {
  title: string;
  description: string;
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: 'default' | 'danger';
}

interface PendingRequest extends ConfirmOptions {
  resolve: (confirmed: boolean) => void;
}

let pushRequest: ((request: PendingRequest | null) => void) | null = null;
let activeResolve: ((confirmed: boolean) => void) | null = null;

const ConfirmServiceHost = () => {
  const [request, setRequest] = useState<PendingRequest | null>(null);
  const viewRef = useRef<PendingRequest | null>(null);
  if (request) viewRef.current = request;
  const view = viewRef.current;

  useEffect(() => {
    pushRequest = setRequest;
    activeResolve = null;
    return () => { pushRequest = null; };
  }, []);

  const settle = (confirmed: boolean) => {
    const current = viewRef.current;
    viewRef.current = null;
    setRequest(null);
    if (current && activeResolve === current.resolve) activeResolve = null;
    current?.resolve(confirmed);
  };

  return (
    <ConfirmDialog
      open={Boolean(request)}
      title={view?.title ?? ''}
      description={view?.description ?? ''}
      confirmLabel={view?.confirmLabel ?? 'Confirm'}
      {...(view?.cancelLabel ? { cancelLabel: view.cancelLabel } : {})}
      {...(view?.tone ? { tone: view.tone } : {})}
      onConfirm={() => settle(true)}
      onCancel={() => settle(false)}
    />
  );
};

export const requestConfirm = (options: ConfirmOptions): Promise<boolean> =>
  new Promise((resolve) => {
    if (!pushRequest) {
      resolve(false);
      return;
    }
    if (activeResolve) activeResolve(false);
    activeResolve = resolve;
    pushRequest({ ...options, resolve });
  });

export const confirmAction = (options: ConfirmOptions): Promise<boolean> => requestConfirm(options);

export const confirmUpdate = (subject: string): Promise<boolean> =>
  requestConfirm({
    title: 'Confirm Update',
    description: `Are you sure you want to save the changes to ${subject}?`,
    confirmLabel: 'Yes, Update',
  });

export const confirmBillCreate = (): Promise<boolean> =>
  requestConfirm({
    title: 'Confirm Bill Creation',
    description: 'Are you sure you want to create this bill? A new bill record will be generated.',
    confirmLabel: 'Yes, Create Bill',
  });

export const confirmBillUpdate = (): Promise<boolean> =>
  requestConfirm({
    title: 'Confirm Bill Update',
    description: 'Are you sure you want to save the changes to this bill?',
    confirmLabel: 'Yes, Update Bill',
  });

export default ConfirmServiceHost;
